import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ProductionPlanView,
  ProductionRequirementView,
} from '@scoutaround/script-breakdown-shared';

import { api, type ProductionPlanExport } from '../../lib/api.js';
import { useRefusal } from '../../lib/permissions.js';
import { Disclosure } from '../ui/Disclosure.js';
import { MoreMenu } from '../ui/MoreMenu.js';
import { StatusPill, type WorkState } from '../ui/StatusPill.js';
import type { PlanAddressPatch } from './address.js';
import { productionPlanKey, usePlan, writeError } from './context.js';
import {
  ASSUMPTION_LABELS,
  COMPARISON_LABELS,
  DEPARTMENT_LABELS,
  METHOD_LABELS,
  REQUIREMENT_TYPE_LABELS,
  RESOURCE_LABELS,
  REVIEW_STATE,
  RISK_LABELS,
  SOURCING_LABELS,
} from './labels.js';
import { IncompleteBanner, PlanLoadState, incompleteReasons, isIncomplete } from './PlanParts.js';

/** How many lines a group shows before "Show all". */
const VISIBLE = 8;

interface Item {
  readonly key: string;
  readonly label: string;
  readonly detail?: string;
  /** Where the item opens; absent, it is a fact with nowhere to go. */
  readonly to?: PlanAddressPatch;
  readonly state?: { state: WorkState; label?: string };
}

interface Group {
  readonly id: string;
  readonly title: string;
  readonly items: readonly Item[];
}

const REVIEW_TYPES = new Set(['SAFETY_REVIEW', 'COMPLIANCE_REVIEW', 'FACT_REVIEW']);

/**
 * THE WHOLE SCREENPLAY'S PLAN, FOR THE PRODUCER — `?view=plan` on the detailed
 * breakdown.
 *
 * Six questions, each its own section, each answered from the plan the server
 * sent and nothing else: what needs my attention, what might change the budget
 * (flags only — the plan has no costs), what is approved, what changed since the
 * last draft, what ScoutAround can help source, and what is still incomplete.
 *
 * An incomplete analysis is said first and never reads as a complete plan.
 * Every item opens where it lives — a requirement on its scene's Breakdown lens
 * with the row in view, an event on the Execution lens — as a new history
 * entry, so Back returns to this overview as it was. Nothing here is a red
 * button: the page's one primary stays in its header.
 */
export function ProducerOverview() {
  const { plan, navigate } = usePlan();

  if (plan === null) {
    return (
      <section className="ui-panel pp-panel" data-testid="production-overview">
        <div className="ui-panel-body">
          <PlanLoadState testId="production-overview" />
        </div>
      </section>
    );
  }

  return (
    <section className="ui-panel pp-panel pp-overview" data-testid="production-overview" aria-labelledby="production-overview-title">
      <div className="ui-panel-head">
        <h3 className="ui-panel-title" id="production-overview-title">
          Production plan
        </h3>
        <span className="ui-toolbar">
          <button
            type="button"
            className="ctl"
            data-testid="production-overview-back"
            onClick={() => {
              navigate({ view: 'breakdown', req: null });
            }}
          >
            Back to the scenes
          </button>
          <ExportControls />
        </span>
      </div>
      <div className="ui-panel-body pp-stack">
        <IncompleteBanner plan={plan} />
        <OverviewSections plan={plan} open={(to) => navigate(to, { push: true })} />
      </div>
    </section>
  );
}

function OverviewSections({ plan, open }: { plan: ProductionPlanView; open: (to: PlanAddressPatch) => void }) {
  const { sceneLabel } = usePlan();

  const requirementItem = (r: ProductionRequirementView, detail?: string): Item => {
    const scene = r.scenes[0]?.sceneId ?? null;

    return {
      key: `req-${r.id}`,
      label: r.name,
      detail: [detail, scene === null ? 'Not tied to a scene' : sceneLabel(scene)].filter((d) => d !== undefined).join(' · '),
      to: scene === null ? undefined : { view: 'breakdown', scene, req: r.id },
      state: REVIEW_STATE[r.reviewStatus],
    };
  };
  const eventScene = (lineageId: string) => plan.events.find((e) => e.lineageId === lineageId)?.sceneId ?? null;
  const live = plan.requirements.filter((r) => r.reviewStatus !== 'REJECTED');

  // 1. What needs my attention
  const attention: Group[] = [
    {
      id: 'pending',
      title: 'Awaiting your decision',
      items: plan.requirements.filter((r) => r.reviewStatus === 'PENDING').map((r) => requirementItem(r)),
    },
    {
      id: 'revalidation',
      title: 'Changed since reviewed',
      items: [
        ...plan.requirements.filter((r) => r.reviewStatus === 'NEEDS_REVALIDATION').map((r) => requirementItem(r)),
        ...plan.events
          .filter((e) => e.reviewStatus === 'NEEDS_REVALIDATION')
          .map((e) => ({
            key: `event-${e.id}`,
            label: e.title,
            detail: `Event · ${sceneLabel(e.sceneId)}`,
            to: { view: 'execution' as const, scene: e.sceneId, req: null },
            state: REVIEW_STATE[e.reviewStatus],
          })),
        ...plan.options
          .filter((o) => o.needsRevalidation)
          .map((o) => {
            const scene = eventScene(o.eventLineageId);

            return {
              key: `option-${o.id}`,
              label: `${METHOD_LABELS[o.method]} method`,
              detail: `The event changed since it was chosen${scene === null ? '' : ` · ${sceneLabel(scene)}`}`,
              to: scene === null ? undefined : { view: 'execution' as const, scene, req: null },
            };
          }),
      ],
    },
    {
      id: 'ambiguous',
      title: 'Same event as before, or a new one?',
      items: plan.events
        .filter((e) => e.matchStatus === 'AMBIGUOUS')
        .map((e) => ({
          key: `event-${e.id}`,
          label: e.title,
          detail: sceneLabel(e.sceneId),
          to: { view: 'execution' as const, scene: e.sceneId, req: null },
        })),
    },
    {
      id: 'assumptions',
      title: 'Assumptions not set',
      items: plan.assumptions
        .filter((a) => a.source === 'NOT_SET')
        .map((a) => ({ key: `assumption-${a.key}`, label: ASSUMPTION_LABELS[a.key], detail: 'Not set' })),
    },
  ];

  // 2. What might change the budget — flags, never a cost.
  const budget: Group[] = [
    {
      id: 'risk',
      title: 'Risk flags',
      items: live
        .filter((r) => r.riskFlags.length > 0 && r.effectiveApplicability !== 'NOT_APPLICABLE')
        .map((r) => requirementItem(r, r.riskFlags.map((f) => RISK_LABELS[f]).join(', '))),
    },
    {
      id: 'method',
      title: 'Depends on a method nobody has chosen',
      items: live.filter((r) => r.effectiveApplicability === 'PENDING_METHOD').map((r) => requirementItem(r, r.condition?.text)),
    },
    {
      id: 'specialist',
      title: 'Duplicates, builds and specialists',
      items: live
        .filter(
          (r) =>
            r.effectiveApplicability !== 'NOT_APPLICABLE' &&
            (r.requirementType === 'CONTINUITY' ||
              r.requirementType === 'SPECIALIST' ||
              r.resourceType === 'SET_BUILD' ||
              r.resourceType === 'SPECIALIST_CREW' ||
              r.specialistReview),
        )
        .map((r) => requirementItem(r, REQUIREMENT_TYPE_LABELS[r.requirementType])),
    },
  ];

  // 3. What is approved
  const approved: Group[] = [
    { id: 'active', title: 'Accepted requirements', items: plan.requirements.filter((r) => r.active).map((r) => requirementItem(r)) },
    {
      id: 'methods',
      title: 'Chosen methods',
      items: plan.options
        .filter((o) => o.status === 'SELECTED')
        .map((o) => {
          const event = plan.events.find((e) => e.lineageId === o.eventLineageId);

          return {
            key: `option-${o.id}`,
            label: `${METHOD_LABELS[o.method]}${event === undefined ? '' : ` — ${event.title}`}`,
            detail: event === undefined ? undefined : sceneLabel(event.sceneId),
            to: event === undefined ? undefined : { view: 'execution' as const, scene: event.sceneId, req: null },
          };
        }),
    },
  ];

  // 4. What changed since the last draft
  const compared = plan.analysis?.comparedScriptVersionId ?? null;
  const changed: Group[] = [
    {
      id: 'changed',
      title: 'New or changed',
      items: plan.requirements
        .filter((r) => r.comparison === 'NEW' || r.comparison === 'CHANGED' || r.comparison === 'UNCERTAIN_MATCH')
        .map((r) => requirementItem(r, r.comparison === null ? undefined : COMPARISON_LABELS[r.comparison])),
    },
    {
      id: 'removed',
      title: 'No longer in this draft',
      items: plan.removedSincePrevious.map((r) => ({
        key: `removed-${r.requirementKey}`,
        label: r.name,
        detail:
          r.wasReviewed === null ? 'Not decided before' : r.wasReviewed === 'ACCEPTED' ? 'Was accepted' : 'Was rejected',
      })),
    },
  ];

  // 5. What ScoutAround can help source
  const resources = live.filter((r) => r.resourceType !== null);
  const locations = resources.filter((r) => r.resourceType === 'LOCATION');
  const source: Group[] = [
    {
      id: 'candidates',
      title: 'Locations with match candidates',
      items: locations
        .filter((r) => r.sourcing?.status === 'MATCH_CANDIDATES_FOUND')
        .map((r) =>
          requirementItem(
            r,
            `${SOURCING_LABELS.MATCH_CANDIDATES_FOUND}${r.sourcing?.candidateCount == null ? '' : ` (${String(r.sourcing.candidateCount)})`}, a candidate is not proof it is available`,
          ),
        ),
    },
    {
      id: 'locations',
      title: 'Other location needs',
      items: locations
        .filter((r) => r.sourcing?.status !== 'MATCH_CANDIDATES_FOUND')
        .map((r) => requirementItem(r, SOURCING_LABELS[r.sourcing?.status ?? 'NOT_SEARCHED'])),
    },
    {
      id: 'outside',
      title: 'To find outside ScoutAround',
      items: resources
        .filter((r) => r.resourceType !== 'LOCATION')
        .map((r) =>
          requirementItem(
            r,
            `${r.resourceType === null ? '' : RESOURCE_LABELS[r.resourceType]} · ${SOURCING_LABELS[r.sourcing?.status ?? 'NOT_SEARCHED']}`,
          ),
        ),
    },
  ];

  // 6. What is still incomplete
  const readings = plan.readings.filter((r) => r.status === 'UNREAD' || r.status === 'FAILED');
  const incomplete: Group[] = [
    {
      id: 'analysis',
      title: 'The reading',
      items: isIncomplete(plan)
        ? incompleteReasons(plan).map((reason, index) => ({ key: `analysis-${String(index)}`, label: reason }))
        : [],
    },
    {
      id: 'readings',
      title: 'Scenes not read',
      items: readings.map((r) => ({
        key: `reading-${r.sceneId}`,
        label: sceneLabel(r.sceneId),
        detail: r.status === 'FAILED' ? 'The reading failed' : 'Not read',
        to: { view: 'breakdown' as const, scene: r.sceneId, req: null },
      })),
    },
    {
      id: 'coverage',
      title: 'What the plan cannot read yet',
      items: plan.coverage
        .filter((c) => c.notSupported.length > 0)
        .map((c) => ({ key: `coverage-${c.department}`, label: DEPARTMENT_LABELS[c.department], detail: c.notSupported.join(', ') })),
    },
    {
      id: 'reviews',
      title: 'Open reviews',
      items: plan.requirements
        .filter((r) => REVIEW_TYPES.has(r.requirementType) && (r.reviewStatus === 'PENDING' || r.reviewStatus === 'NEEDS_REVALIDATION'))
        .map((r) => requirementItem(r, REQUIREMENT_TYPE_LABELS[r.requirementType])),
    },
  ];

  return (
    <div className="pp-overview-grid">
      <OverviewSection id="attention" title="What needs my attention" groups={attention} open={open} empty="Nothing waits on you." />
      <OverviewSection
        id="budget"
        title="What might change the budget"
        lede="Flags only: the plan has no costs."
        groups={budget}
        open={open}
        empty="Nothing flagged."
      />
      <OverviewSection id="approved" title="What is approved" groups={approved} open={open} empty="Nothing accepted or chosen yet." />
      <OverviewSection
        id="changed"
        title="What changed"
        groups={changed}
        open={open}
        empty={compared === null ? 'There is no earlier draft to compare with.' : 'Nothing changed since the last draft.'}
      />
      <OverviewSection
        id="source"
        title="What ScoutAround can help source"
        lede="ScoutAround searches its location catalogue. A match is a candidate, not proof it is available; everything else is found outside ScoutAround."
        groups={source}
        open={open}
        empty="Nothing in the plan names a resource to find."
      />
      <OverviewSection
        id="incomplete"
        title="What is still incomplete"
        groups={incomplete}
        open={open}
        empty="Nothing the plan knows of is missing."
      />
    </div>
  );
}

function OverviewSection({
  id,
  title,
  lede,
  groups,
  open,
  empty,
}: {
  id: string;
  title: string;
  lede?: string;
  groups: readonly Group[];
  open: (to: PlanAddressPatch) => void;
  empty: string;
}) {
  const filled = groups.filter((g) => g.items.length > 0);
  const total = filled.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <section className="pp-section" data-testid={`production-overview-section-${id}`} aria-labelledby={`production-overview-${id}-title`}>
      <h4 className="pp-section-title" id={`production-overview-${id}-title`}>
        {title}
        {total === 0 ? null : <span className="pp-count" data-testid={`production-overview-count-${id}`}>{String(total)}</span>}
      </h4>
      {lede === undefined ? null : <p className="mm pp-note">{lede}</p>}
      {filled.length === 0 ? (
        <p className="mm pp-note" data-testid={`production-overview-empty-${id}`}>
          {empty}
        </p>
      ) : (
        filled.map((group) => (
          <div key={group.id} className="pp-subgroup" data-testid={`production-overview-group-${id}-${group.id}`}>
            <p className="pp-subgroup-title">{`${group.title} · ${String(group.items.length)}`}</p>
            <ItemList prefix={`${id}-${group.id}`} items={group.items.slice(0, VISIBLE)} open={open} />
            {group.items.length > VISIBLE ? (
              <Disclosure
                summary={`Show ${String(group.items.length - VISIBLE)} more`}
                testId={`production-overview-show-more-${id}-${group.id}`}
              >
                <ItemList prefix={`${id}-${group.id}`} items={group.items.slice(VISIBLE)} open={open} />
              </Disclosure>
            ) : null}
          </div>
        ))
      )}
    </section>
  );
}

/** `prefix` is the section and group: one requirement can stand in several groups. */
function ItemList({
  prefix,
  items,
  open,
}: {
  prefix: string;
  items: readonly Item[];
  open: (to: PlanAddressPatch) => void;
}) {
  return (
    <ul className="pp-items">
      {items.map((item) => {
        const body: ReactNode = (
          <>
            <span className="pp-item-label">{item.label}</span>
            {item.state === undefined ? null : <StatusPill state={item.state.state} label={item.state.label} size="sm" />}
            {item.detail === undefined || item.detail === '' ? null : <span className="pp-item-detail mm">{item.detail}</span>}
          </>
        );
        const to = item.to;

        return (
          <li key={item.key}>
            {to === undefined ? (
              <span className="pp-item" data-testid={`production-overview-item-${prefix}-${item.key}`}>
                {body}
              </span>
            ) : (
              <button
                type="button"
                className="pp-item pp-item-link"
                data-testid={`production-overview-item-${prefix}-${item.key}`}
                onClick={() => {
                  open(to);
                }}
              >
                {body}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The structured hand-off as a file, and a re-derivation from the stored reading. */
function ExportControls() {
  const { projectId, scriptVersionId } = usePlan();
  const queryClient = useQueryClient();
  const refusal = useRefusal('SCRIPT_REVIEW');

  const exportPlan = useMutation({
    mutationFn: (format: 'json' | 'csv') => api.exportProductionPlan(projectId, scriptVersionId, format),
    onSuccess: saveFile,
  });
  const recompute = useMutation({
    mutationFn: () => api.recomputeProductionPlan(projectId, scriptVersionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productionPlanKey(projectId, scriptVersionId) }),
  });
  const error = writeError(exportPlan.error ?? recompute.error);

  return (
    <>
      <button
        type="button"
        className="ctl"
        data-testid="production-export-json"
        disabled={exportPlan.isPending}
        onClick={() => {
          exportPlan.mutate('json');
        }}
      >
        Export JSON
      </button>
      <button
        type="button"
        className="ctl"
        data-testid="production-export-csv"
        disabled={exportPlan.isPending}
        onClick={() => {
          exportPlan.mutate('csv');
        }}
      >
        Export CSV
      </button>
      <MoreMenu
        testId="production-overview-more"
        label="More plan actions"
        items={[
          {
            id: 'recompute',
            label: recompute.isPending ? 'Working it out again…' : 'Work the plan out again from the reading',
            disabled: recompute.isPending || refusal !== null,
            hint: refusal ?? 'No new AI request: it uses the reading already stored.',
            testId: 'production-recompute',
            onSelect: () => {
              recompute.mutate();
            },
          },
        ]}
      />
      {error === null ? null : (
        <span className="pp-error" role="alert" data-testid="production-overview-error">
          {error}
        </span>
      )}
    </>
  );
}

/** Save the server's text as a file in the browser. */
function saveFile(file: ProductionPlanExport): void {
  if (typeof URL.createObjectURL !== 'function') return;

  const url = URL.createObjectURL(new Blob([file.body], { type: file.contentType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}
